import { Injectable } from '@angular/core';
import { Address, Credential, DeliveryZone, Schedule, Store as StoreModel } from '@core/models';
import { OrderTypeEnum } from '@core/enums';
import { StoresFactory } from './stores-factory.service';
import {
	AddressConfirmationDecision,
	IStoreStatus,
	StoreProcessingInput,
	StoreProcessingResult,
	StoresOverviewResult,
	StoreScheduleRange,
} from './interfaces';
import {
	checkStoreStatus,
	evaluateAddressConfirmation,
	getStoreScheduleRange,
	getStoreScheduleTime,
	isClientAuthenticated,
	isDeliveryAddressAvailable,
	shouldGeocodeAddress,
} from './rules';
import {
	formatClientAddress,
	formatFullAddress,
	isValidAddressQuery,
} from './utils';

@Injectable({ providedIn: 'root' })
export class StoresProcessor {
	constructor(private readonly factory: StoresFactory) {}

	processStores(input: StoreProcessingInput, orderType: string = OrderTypeEnum.Pickup): StoreProcessingResult {
		const strategy = this.factory.getStrategy(orderType);
		return strategy.process(input);
	}

	processStoresOverview(input: StoreProcessingInput): StoresOverviewResult {
		const pickupStrategy = this.factory.getPickupStrategy();
		const deliveryStrategy = this.factory.getDeliveryStrategy();

		const pickupResult = pickupStrategy.process(input);
		const deliveryResult = deliveryStrategy.process(input);

		return {
			storesWithDistance: pickupResult.stores,
			deliveryStores: deliveryResult.stores,
			deliveryZonesAvailable: deliveryResult.deliveryZonesAvailable ?? [],
		};
	}

	getStoreStatus(horarios: Schedule[], referenceDate?: Date): IStoreStatus {
		return checkStoreStatus(horarios, referenceDate);
	}

	getRangeTimeByDay(
		store: StoreModel,
		code: string,
		referenceDate?: Date
	): StoreScheduleRange | null {
		return getStoreScheduleRange(store, code, referenceDate);
	}

	getStoreScheduleTime(
		store: StoreModel,
		code: string,
		isAvailable: boolean,
		referenceDate?: Date
	): string {
		return getStoreScheduleTime(store, code, isAvailable, referenceDate);
	}

	isAddressAvailable(
		deliveryZones: DeliveryZone[] | null | undefined,
		currentStore?: StoreModel | null
	): boolean {
		return isDeliveryAddressAvailable(deliveryZones, currentStore);
	}

	formatClientAddress(
		rawAddress: string,
		apartment?: string | null,
		addressName?: string | null
	): Address {
		return formatClientAddress(rawAddress, apartment, addressName);
	}

	formatFullAddress(address: Address): string {
		return formatFullAddress(address);
	}

	isValidAddressQuery(query?: string | null): boolean {
		return isValidAddressQuery(query);
	}

	shouldGeocodeAddress(newAddress?: string | null, currentAddress?: string | null): boolean {
		return shouldGeocodeAddress(newAddress, currentAddress);
	}

	evaluateAddressConfirmation(params: {
		isLoggedIn: boolean;
		showNameAddressInput: boolean;
		isAddressAvailable: boolean;
	}): AddressConfirmationDecision {
		return evaluateAddressConfirmation(params);
	}

	isClientAuthenticated(auth?: Credential | null): boolean {
		return isClientAuthenticated(auth);
	}
}
